'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, AlertBody, Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import { useEffect, useId, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useCustomerAutoshipSubscriptions, useDecisions } from '@libs/common-next';
import { AutoshipInfoDrawer } from '../AutoshipInfoDrawer';
import { CartConfirmDrawer } from '../CartConfirmDrawer';
import { PDPStoreDrawer } from '../pdp-store-drawer';
import { SddAddressDrawer } from '../SddAddressDrawer';
import { isCustomerFirstAutoshipEligible } from './lib';
import { MiniPdpProductInfo } from './mini-pdp-product-info';
import { MiniPdpPurchaseForm } from './mini-pdp-purchase-form';
import { MiniPdpSkeleton } from './mini-pdp-skeleton';
import { useMiniPdpDrawer } from './model/use-mini-pdp-drawer';
import { useMiniPdpErrorAnalytics } from './model/use-mini-pdp-error-analytics';
import { useMiniPdpForm } from './model/use-mini-pdp-form';
import { useMiniPdpProduct } from './model/use-mini-pdp-product';
import type { MiniPdpDrawerProps } from './types';
import './mini-pdp-drawer.scss';
export function MiniPdpDrawer(t0) {
  const $ = _c(66);
  const {
    className
  } = t0;
  const isOpen = useMiniPdpDrawer(_temp);
  const close = useMiniPdpDrawer(_temp2);
  const {
    base,
    tailored,
    isLoading,
    isError,
    isUnexpectedError
  } = useMiniPdpProduct();
  const defaultProduct = base?.variants[base.defaultSku];
  const purchaseTailored = isError ? null : tailored;
  const form = useMiniPdpForm(defaultProduct, base?.defaultSku);
  const [isSddAddressOpen, setIsSddAddressOpen] = useState(false);
  let t1;
  if ($[0] !== form.control) {
    t1 = {
      control: form.control,
      name: "selectedSku"
    };
    $[0] = form.control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedSku = useWatch(t1);
  const resolvedSku = base?.variants[selectedSku] ? selectedSku : base?.defaultSku;
  const selectedProduct = resolvedSku && base ? base.variants[resolvedSku] : undefined;
  const [isStoreDrawerOpen, setIsStoreDrawerOpen] = useState(false);
  const [isAutoshipInfoOpen, setIsAutoshipInfoOpen] = useState(false);
  const isAutoshipEnabled = useDecisions("as_mini_pdp");
  const {
    autoshipData
  } = useCustomerAutoshipSubscriptions();
  const [isAddToCartError, setIsAddToCartError] = useState(false);
  const [isSddAddressError, setIsSddAddressError] = useState(false);
  const showError = isError || isAddToCartError;
  useMiniPdpErrorAnalytics(isUnexpectedError || isAddToCartError || isSddAddressError);
  const headingId = useId();
  const [hasBeenRendered, setHasBeenRendered] = useState(false);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setHasBeenRendered(true);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (!isOpen && isAutoshipInfoOpen) {
    setIsAutoshipInfoOpen(false);
  }
  const t4 = !isOpen;
  let t5;
  if ($[4] !== close || $[5] !== t4) {
    t5 = <CartConfirmDrawer stacked={true} forceClosed={t4} onCloseAll={close} />;
    $[4] = close;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const cartConfirmDrawer = t5;
  if (!hasBeenRendered) {
    return cartConfirmDrawer;
  }
  let t6;
  if ($[7] !== close || $[8] !== isAutoshipInfoOpen || $[9] !== isSddAddressOpen || $[10] !== isStoreDrawerOpen) {
    t6 = () => !isSddAddressOpen && !isStoreDrawerOpen && !isAutoshipInfoOpen && close();
    $[7] = close;
    $[8] = isAutoshipInfoOpen;
    $[9] = isSddAddressOpen;
    $[10] = isStoreDrawerOpen;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = className ? `mini-pdp-drawer ${className}` : "mini-pdp-drawer";
  let t8;
  if ($[12] !== headingId) {
    t8 = <Drawer.Header className="mini-pdp-drawer__header"><Heading id={headingId} tagName="h2" size="headline" data-testid="mini-pdp-drawer-title">Choose Options</Heading></Drawer.Header>;
    $[12] = headingId;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = base ? "true" : "false";
  const t10 = purchaseTailored ? "true" : "false";
  let t11;
  if ($[14] !== showError) {
    t11 = showError && <Alert variant="error" iconName="exclamation-circle-fill" className="mini-pdp-drawer__error" data-testid="mini-pdp-error" dismissible={false}><AlertBody><Heading tagName="h2" size="body-bold">Unexpected error has occurred</Heading><TextPassage capLinelength={false}>Please refresh page to continue.</TextPassage></AlertBody></Alert>;
    $[14] = showError;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== isLoading || $[17] !== selectedProduct || $[18] !== showError) {
    t12 = (isLoading || showError) && !selectedProduct && <MiniPdpSkeleton />;
    $[16] = isLoading;
    $[17] = selectedProduct;
    $[18] = showError;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== base || $[21] !== isError || $[22] !== isLoading || $[23] !== purchaseTailored || $[24] !== selectedProduct) {
    t13 = selectedProduct && <div className="mini-pdp-drawer__options"><MiniPdpProductInfo product={selectedProduct} /><MiniPdpPurchaseForm base={base} product={selectedProduct} tailored={purchaseTailored} isLoading={isLoading} isError={isError} onErrorChange={setIsAddToCartError} onOpenSddAddress={() => setIsSddAddressOpen(true)} onOpenStoreDrawer={() => setIsStoreDrawerOpen(true)} onOpenAutoshipInfo={() => setIsAutoshipInfoOpen(true)} /></div>;
    $[20] = base;
    $[21] = isError;
    $[22] = isLoading;
    $[23] = purchaseTailored;
    $[24] = selectedProduct;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t9) {
    t14 = <Drawer.Body className="mini-pdp-drawer__body" data-testid="mini-pdp-drawer-body"><div className="mini-pdp-drawer__product" data-testid="mini-pdp-product-data" data-base-loaded={t9} data-tailored-loaded={t10}>{t11}{t12}{t13}</div></Drawer.Body>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== headingId || $[33] !== isOpen || $[34] !== t14 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t15 = <Drawer align="right" isActive={isOpen} onClose={t6} data-testid="mini-pdp-drawer" className={t7} ariaLabelledBy={headingId} dismissible={true}>{t8}{t14}</Drawer>;
    $[32] = headingId;
    $[33] = isOpen;
    $[34] = t14;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const t16 = !isOpen;
  const t17 = Boolean(resolvedSku && tailored?.variants?.[resolvedSku]?.SDD?.doorDashValidationSuccess);
  let t18;
  if ($[39] !== close || $[40] !== isError || $[41] !== isLoading || $[42] !== isSddAddressOpen || $[43] !== t16 || $[44] !== t17) {
    t18 = <SddAddressDrawer showBackButton={true} showTrigger={false} isOpen={isSddAddressOpen} onOpenChange={setIsSddAddressOpen} onErrorChange={setIsSddAddressError} forceClosed={t16} onCloseAll={close} isDeliveryAvailable={t17} isLoading={isLoading} isError={isError} />;
    $[39] = close;
    $[40] = isError;
    $[41] = isLoading;
    $[42] = isSddAddressOpen;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = !isOpen;
  const t20 = selectedProduct?.deliveryMethods?.BOPIS?.purchaseQuantities?.min;
  const t21 = resolvedSku ? _temp4 : undefined;
  let t22;
  if ($[46] !== close || $[47] !== isStoreDrawerOpen || $[48] !== resolvedSku || $[49] !== t19 || $[50] !== t20 || $[51] !== t21) {
    t22 = <PDPStoreDrawer showBackButton={true} forceClosed={t19} onCloseAll={close} isActive={isStoreDrawerOpen} onOpenChange={setIsStoreDrawerOpen} sku={resolvedSku} drawerHeading="Pick up in store" toggleText="Only stores with item availability" className="pdp-page" minPurchaseQuantity={t20} filterFn={t21} />;
    $[46] = close;
    $[47] = isStoreDrawerOpen;
    $[48] = resolvedSku;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== autoshipData || $[54] !== close || $[55] !== isAutoshipEnabled || $[56] !== isAutoshipInfoOpen || $[57] !== selectedProduct?.autoship) {
    t23 = isAutoshipEnabled && <AutoshipInfoDrawer showBackButton={true} discountDisplay={selectedProduct?.autoship.discountDisplay} isFirstAutoshipEligible={isCustomerFirstAutoshipEligible(autoshipData)} isDrawerOpen={isAutoshipInfoOpen} setIsDrawerOpen={setIsAutoshipInfoOpen} onCloseAll={close} />;
    $[53] = autoshipData;
    $[54] = close;
    $[55] = isAutoshipEnabled;
    $[56] = isAutoshipInfoOpen;
    $[57] = selectedProduct?.autoship;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== cartConfirmDrawer || $[60] !== form || $[61] !== t15 || $[62] !== t18 || $[63] !== t22 || $[64] !== t23) {
    t24 = <FormProvider {...form}>{t15}{t18}{t22}{t23}{cartConfirmDrawer}</FormProvider>;
    $[59] = cartConfirmDrawer;
    $[60] = form;
    $[61] = t15;
    $[62] = t18;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  return t24;
}
function _temp4(stores) {
  return stores.filter(_temp3);
}
function _temp3(store) {
  return store.attributes?.bopisEnabled;
}
function _temp2(state_0) {
  return state_0.close;
}
function _temp(state) {
  return state.isOpen;
}